import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2, Lock } from 'lucide-react';
import { ApiError, getMe, login } from './api';

/**
 * Password-only login for the single admin operator. POSTs to
 * `/v1/admin/login`, which sets the httpOnly session cookie and redirects
 * to `/admin`. Already-logged-in admins skip the form entirely.
 */
export default function AdminLogin() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [reveal, setReveal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // If the session cookie is still valid, jump straight to /admin.
  useEffect(() => {
    let cancelled = false;
    getMe()
      .then(() => {
        if (!cancelled) navigate('/admin', { replace: true });
      })
      .catch(() => {
        /* not logged in — render the form */
      });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      await login(password);
      navigate('/admin', { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 429) {
        setError('Too many attempts. Try again later.');
      } else if (err instanceof ApiError && err.status === 401) {
        setError('Wrong password.');
      } else {
        setError('Login failed. Try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-brand-ink bg-hero-radial bg-[length:200%_200%] p-6">
      <form
        onSubmit={handleSubmit}
        className="glass-card w-full max-w-md p-8 shadow-glow"
      >
        <div className="mb-6 flex flex-col items-center gap-3 text-center">
          <img src="/favicon.png" alt="" className="h-12 w-12" />
          <h1 className="font-display text-2xl font-bold text-white">
            Dinar Pulse Admin
          </h1>
          <p className="text-sm text-slate-400">
            Sign in with the admin password to manage the in-app ad banner.
          </p>
        </div>

        <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-400">
          Password
        </label>
        <div className="relative">
          <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <input
            type={reveal ? 'text' : 'password'}
            autoFocus
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Admin password"
            className="w-full rounded-xl border border-white/10 bg-black/30 px-10 py-3 text-sm text-white placeholder:text-slate-500 focus:border-brand-cyan focus:outline-none focus:ring-2 focus:ring-brand-cyan/30"
          />
          <button
            type="button"
            onClick={() => setReveal((v) => !v)}
            aria-label={reveal ? 'Hide password' : 'Show password'}
            className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-1 text-slate-500 hover:text-slate-300"
          >
            {reveal ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          </button>
        </div>

        {error && (
          <p className="mt-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs font-medium text-red-300">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting || password.length === 0}
          className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-brand-cyan px-4 py-3 text-sm font-bold text-brand-ink transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Signing in…
            </>
          ) : (
            'Sign in'
          )}
        </button>
      </form>
    </div>
  );
}
